import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { teamService, type TeamMember } from '@services/teamService';
import type { RootState } from '../index';

interface TeamState {
  members: TeamMember[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

const initialState: TeamState = { members: [], isLoading: false, isSaving: false, error: null };

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

export const fetchTeamMembers = createAsyncThunk(
  'team/fetchMembers',
  async (_, { rejectWithValue }) => {
    try {
      return await teamService.getMembers();
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to load team members'));
    }
  }
);

export const inviteTeamMember = createAsyncThunk(
  'team/inviteMember',
  async (email: string, { rejectWithValue }) => {
    try {
      return await teamService.inviteMember(email);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to invite team member'));
    }
  }
);

export const removeTeamMember = createAsyncThunk(
  'team/removeMember',
  async (membershipId: string, { rejectWithValue }) => {
    try {
      await teamService.removeMember(membershipId);
      return membershipId;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to remove team member'));
    }
  }
);

const teamSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchTeamMembers.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTeamMembers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.members = action.payload;
      })
      .addCase(fetchTeamMembers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(inviteTeamMember.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(inviteTeamMember.fulfilled, (state, action) => {
        state.isSaving = false;
        state.members.push(action.payload);
      })
      .addCase(inviteTeamMember.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      .addCase(removeTeamMember.fulfilled, (state, action) => {
        state.members = state.members.filter(member => member.id !== action.payload);
      });
  },
});

export const selectTeamMembers = (state: RootState) => state.team.members;
export const selectTeamLoading = (state: RootState) => state.team.isLoading;
export const selectTeamSaving = (state: RootState) => state.team.isSaving;

export default teamSlice.reducer;
